import classNames from 'classnames';
import {
  type FC,
  type FormEventHandler,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import Icon from 'components/Icon/Icon';
import { useSearchQuery } from 'utils/location';
import { useTranslation } from 'utils/translations';

import styles from './SearchField.module.scss';

type SearchFieldProps = {
  placeholder?: string;
  minLength?: number;
  className?: string;
};

const SEARCH_PARAM = 'q';

const SearchField: FC<SearchFieldProps> = ({
  placeholder = 'search',
  minLength = 3,
  className,
}) => {
  const placeholderText = useTranslation(placeholder);
  const [query, setQuery] = useSearchQuery();
  const [value, setValue] = useState(query.get(SEARCH_PARAM) ?? '');
  const inputRef = useRef<HTMLInputElement>(null);

  const onSubmit = useCallback<FormEventHandler>((e) => {
    e.preventDefault();
    inputRef.current?.blur();
  }, []);

  useEffect(() => {
    if (value.length >= minLength && value !== query.get(SEARCH_PARAM)) {
      setQuery({
        [SEARCH_PARAM]: value,
      });
    }
    if (value.length === 0 && query.get(SEARCH_PARAM)) {
      setQuery({
        [SEARCH_PARAM]: null,
      });
    }
  }, [value, minLength, setQuery, query]);

  return (
    <form
      onSubmit={onSubmit}
      name="search"
      className={classNames(styles.wrapper, className)}
    >
      <label>
        <Icon name="search-normal.outline" className={styles.icon} />
        <input
          className={styles['search-field']}
          type="text"
          value={value}
          onChange={({ currentTarget }) => setValue(currentTarget.value)}
          placeholder={placeholderText}
          ref={inputRef}
        />
      </label>
      {value.length > 0 && (
        <button
          className={styles['reset-button']}
          type="reset"
          onClick={() => setValue('')}
        >
          <Icon name="close.outline" width={16} height={16} />
        </button>
      )}
    </form>
  );
};

export default SearchField;
